<template>
    <div
        class="tiles"
        :class="[`tiles--${layout}`, { 'tiles--dense': dense }]"
        role="group"
    >
        <button
            v-for="(tile, index) in tiles"
            :key="index"
            type="button"
            class="tile"
            :class="{ 'is-active': currentTile == tile.value, 'tile--bare': !tile.subTitle && !tile.iconSrc }"
            :aria-pressed="currentTile == tile.value"
            :style="{ '--tile-index': index }"
            @click="tileClick(tile.value)"
        >
            <span v-if="iconFor(tile)" class="tile-logo">
                <img :src="iconFor(tile)" :alt="tile.title" loading="lazy" />
            </span>
            <span class="tile-text">
                <span class="tile-title">{{ tile.title }}</span>
                <span v-if="tile.subTitle" class="tile-sub">{{ tile.subTitle }}</span>
            </span>
            <span class="tile-mark" aria-hidden="true"><i class="fa fa-check"></i></span>
        </button>
    </div>
</template>

<script>
export default {
    name: "Tiles",
    props: {
        selectedTile: [String, Boolean, Number],
        tiles: {
            type: Array,
            default: () => []
        },
        // "grid" keeps the classic card grid, "list" stacks full-width rows -
        // easier to scan when options carry a sentence of explanation
        layout: {
            type: String,
            default: "grid"
        },
        // Tighter type and spacing for tiles rendered inside a modal
        dense: {
            type: Boolean,
            default: false
        }
    },
    emits: ['onSelectTile'],
    data() {
        return {
            currentTile: this.selectedTile
        }
    },
    watch: {
        selectedTile(val) {
            this.currentTile = val
        }
    },
    methods: {
        tileClick(val) {
            // Clicking the selected tile clears it, same contract as before:
            // the value is emitted on select and null on deselect
            this.currentTile = this.currentTile == val ? null : val;
            this.$emit('onSelectTile', this.currentTile);
        },
        iconFor(tile) {
            if (!tile.iconSrc) return null;
            if (tile.iconSrc.includes('base64')) return tile.iconSrc;
            return this.getURL(tile.iconSrc);
        },
        getURL(assetName) {
            try {
                return require("../assets/" + assetName)
            } catch (e) {
                return null
            }
        }
    }
}
</script>

<style scoped>
.tiles {
    --tile-bg: #ffffff;
    --tile-bg-alt: #FBFCFE;
    --tile-border: #E3EBF6;
    --tile-border-hover: #B1C2D9;
    --tile-title: #12263F;
    --tile-sub: #6E84A3;
    --tile-accent: #2C7BE5;
    --tile-accent-ink: #1657B0;
    --tile-accent-wash: rgba(44, 123, 229, 0.06);
    --tile-ring: rgba(44, 123, 229, 0.16);
    --tile-shadow: rgba(18, 38, 63, 0.14);

    display: grid;
    gap: 0.75rem;
    max-width: 62rem;
    /* Note: the bottom margin stands in for the mb-5 the old markup hard-coded,
       so the pages that share this component keep their spacing */
    margin: 0 auto 3rem;
}

/* A max track width keeps a two-option row from stretching into two banners,
   while auto-fit still fills wide containers. The lower bound is kept small
   enough that four options fit one row in a modal instead of wrapping a single
   orphan tile onto a second line. */
.tiles--grid {
    grid-template-columns: repeat(auto-fit, minmax(10.5rem, 13rem));
    justify-content: center;
}

.tiles--grid.tiles--dense {
    grid-template-columns: repeat(auto-fit, minmax(9.5rem, 11.5rem));
    gap: 0.5rem;
}

/* Options that are a bare label - no description, no logo - read as chips
   rather than cards, so they are centred and given far less height */
.tiles--grid .tile--bare {
    align-items: center;
    justify-content: center;
    min-height: 3.25rem;
    /* Symmetric inline padding so the centred label stays centred once the
       check chip appears in the corner */
    padding: 0.625rem 1.75rem;
}

.tiles--grid .tile--bare .tile-text {
    align-items: center;
}

.tiles--grid .tile--bare .tile-title {
    padding-right: 0;
    text-align: center;
}

.tiles--list {
    grid-template-columns: repeat(auto-fit, minmax(19rem, 1fr));
    max-width: 46rem;
    gap: 0.5rem;
}

.tiles--list.tiles--dense {
    gap: 0.375rem;
}

.tile {
    position: relative;
    display: flex;
    gap: 0.625rem;
    padding: 0.8125rem 0.875rem;
    text-align: left;
    color: var(--tile-sub);
    background-color: var(--tile-bg);
    background-image: linear-gradient(180deg, var(--tile-bg) 0%, var(--tile-bg-alt) 100%);
    border: 1px solid var(--tile-border);
    border-radius: 0.5rem;
    cursor: pointer;
    transition: border-color 0.16s ease, box-shadow 0.16s ease,
        transform 0.16s ease, background-color 0.16s ease;
    animation: tile-in 0.32s cubic-bezier(0.16, 0.84, 0.44, 1) both;
    animation-delay: calc(var(--tile-index, 0) * 22ms);
}

.tiles--grid .tile {
    flex-direction: column;
}

.tiles--list .tile {
    align-items: flex-start;
    padding: 0.6875rem 0.875rem;
}

.tile:hover {
    border-color: var(--tile-border-hover);
    box-shadow: 0 8px 18px -12px var(--tile-shadow);
    transform: translateY(-1px);
}

.tile:focus-visible {
    outline: 2px solid var(--tile-accent);
    outline-offset: 2px;
}

.tile.is-active {
    border-color: var(--tile-accent);
    background-image: none;
    background-color: var(--tile-accent-wash);
    box-shadow: 0 0 0 3px var(--tile-ring);
}

.tile-text {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    min-width: 0;
}

.tile-title {
    padding-right: 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.005em;
    color: var(--tile-title);
}

.tile.is-active .tile-title {
    color: var(--tile-accent-ink);
}

.tile-sub {
    font-size: 0.75rem;
    line-height: 1.45;
    color: var(--tile-sub);
}

.tiles--dense .tile-title {
    font-size: 0.8125rem;
}

.tiles--dense .tile-sub {
    font-size: 0.6875rem;
    line-height: 1.4;
}

.tile-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0.25rem;
    background-color: #ffffff;
    border: 1px solid var(--tile-border);
    border-radius: 0.375rem;
    overflow: hidden;
}

.tiles--grid .tile-logo {
    margin-bottom: 0.5rem;
}

.tile-logo img {
    width: 100%;
    height: 1.5rem;
    object-fit: contain;
}

/* The check is always in the layout so selecting a tile does not shift text */
.tile-mark {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.125rem;
    height: 1.125rem;
    font-size: 0.5rem;
    color: #ffffff;
    background-color: var(--tile-accent);
    border-radius: 50%;
    opacity: 0;
    transform: scale(0.4);
    transition: opacity 0.16s ease, transform 0.16s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.tile.is-active .tile-mark {
    opacity: 1;
    transform: scale(1);
}

@keyframes tile-in {
    from {
        opacity: 0;
        transform: translateY(6px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .tile {
        animation: none;
        transition: none;
    }

    .tile:hover {
        transform: none;
    }
}
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode .tiles {
    --tile-bg: #{$dark-input-bg};
    --tile-bg-alt: #1e2127;
    --tile-border: #{$dark-border-color};
    --tile-border-hover: #3d434c;
    --tile-title: #{$dark-text-primary};
    --tile-sub: #{$dark-text-secondary};
    --tile-accent: #4d90ea;
    --tile-accent-ink: #a8caf8;
    --tile-accent-wash: rgba(44, 123, 229, 0.14);
    --tile-ring: rgba(77, 144, 234, 0.22);
    --tile-shadow: rgba(0, 0, 0, 0.55);

    .tile-logo {
        background-color: #{$dark-elevated-bg};
    }
}
</style>
